<script setup>
/**
 * @fileoverview SharePortalVMs - Lab view page for external users
 *
 * Displays shared lab with tabs for VMs and Files.
 * Similar layout to LabDetailPage but simplified for external users.
 *
 * @example
 * Route: /share/:shareId/vms
 */

import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  LogOut,
  Play,
  Square,
  RefreshCw,
  Monitor,
  Clock,
  AlertTriangle,
  Loader2,
  Server,
  FolderOpen
} from 'lucide-vue-next'
import { useShareSession } from '@/composables/useShareSession'
import { getShareVMs, controlShareVMPower } from '@/api/sharePortal'
import ShareFileExplorer from '@/components/files/ShareFileExplorer.vue'
import Button from '@/components/ui/button/Button.vue'
import Card from '@/components/ui/card/Card.vue'
import CardHeader from '@/components/ui/card/CardHeader.vue'
import CardTitle from '@/components/ui/card/CardTitle.vue'
import CardContent from '@/components/ui/card/CardContent.vue'
import Alert from '@/components/ui/Alert.vue'
import AlertDescription from '@/components/ui/AlertDescription.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import Tabs from '@/components/ui/Tabs.vue'
import TabsList from '@/components/ui/TabsList.vue'
import TabsTrigger from '@/components/ui/TabsTrigger.vue'
import TabsContent from '@/components/ui/TabsContent.vue'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'

const route = useRoute()
const router = useRouter()

// Get share ID from route params
const shareId = computed(() => route.params.shareId)

// State
const vms = ref([])
const isLoading = ref(true)
const error = ref(null)
const isActionPending = ref(new Set())
const activeTab = ref('vms')

// Share session composable
const {
  labId,
  labName,
  timeRemainingFormatted,
  isExpired,
  isExpiringSoon,
  checkSession,
  logout,
  loadShareInfo
} = useShareSession()

// Polling configuration - default 5 minutes
const VM_POLL_INTERVAL = import.meta.env.VITE_SHARE_VM_POLL_INTERVAL
  ? parseInt(import.meta.env.VITE_SHARE_VM_POLL_INTERVAL, 10)
  : 300000

let vmPollingInterval = null
let powerPollingInterval = null

const POWER_POLL_INTERVAL = 5000
const VM_TRANSITIONAL_STATES = ['starting', 'stopping', 'pending']

function startPowerPolling() {
  if (powerPollingInterval) return
  powerPollingInterval = setInterval(async () => {
    await fetchVMs(false)
    const stillTransitional = vms.value.some(vm =>
      VM_TRANSITIONAL_STATES.includes(vm.status?.toLowerCase()),
    )
    if (!stillTransitional) {
      stopPowerPolling()
    }
  }, POWER_POLL_INTERVAL)
}

function stopPowerPolling() {
  if (powerPollingInterval) {
    clearInterval(powerPollingInterval)
    powerPollingInterval = null
  }
}

/**
 * Check authentication on mount
 */
onMounted(async () => {
  if (!checkSession()) {
    return
  }

  // Always load share info to get lab ID and name (ensures fresh data on page refresh)
  if (shareId.value) {
    try {
      const info = await loadShareInfo(shareId.value)
      console.debug('[SharePortalVMs] Share info loaded:', info)
    } catch (err) {
      console.error('[SharePortalVMs] Failed to load share info:', err)
    }
  }

  await fetchVMs()

  // Start polling for VM status updates (silent refresh)
  vmPollingInterval = setInterval(() => {
    fetchVMs(false)
  }, VM_POLL_INTERVAL)
})

/**
 * Cleanup on unmount
 */
onBeforeUnmount(() => {
  if (vmPollingInterval) {
    clearInterval(vmPollingInterval)
  }
  stopPowerPolling()
})

/**
 * Fetch VMs for the share
 */
async function fetchVMs(showLoading = true) {
  try {
    if (showLoading) {
      isLoading.value = true
    }
    error.value = null
    const vmList = await getShareVMs(shareId.value)
    vms.value = vmList
  } catch (err) {
    console.error('Failed to fetch VMs:', err)
    error.value = err.message || 'Failed to load virtual machines'
  } finally {
    isLoading.value = false
  }
}

/**
 * Control VM power (start/stop/restart)
 */
async function handlePowerControl(vmId, action) {
  try {
    isActionPending.value.add(vmId)
    await controlShareVMPower(shareId.value, vmId, action)
    const transitionalMap = { start: 'starting', stop: 'stopping', restart: 'stopping' }
    const vm = vms.value.find(v => v.id === vmId)
    if (vm) {
      vm.status = transitionalMap[action]
    }
    startPowerPolling()
  } catch (err) {
    console.error(`Failed to ${action} VM:`, err)
    error.value = err.message || `Failed to ${action} VM`
  } finally {
    isActionPending.value.delete(vmId)
  }
}

/**
 * Navigate to console page
 */
function openConsole(vmId) {
  router.push(`/share/${shareId.value}/vms/${vmId}/console`)
}

/**
 * Handle logout
 */
async function handleLogout() {
  await logout()
}

/**
 * Get status badge classes
 */
function getStatusBadgeClasses(status) {
  const statusMap = {
    running: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400',
    stopped: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    starting: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
    stopping: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
    error: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
  }
  return statusMap[status] || statusMap.stopped
}

/**
 * Check if VM action is pending
 */
function isVMPending(vmId) {
  return isActionPending.value.has(vmId)
}
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Header -->
    <header
      class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3"
    >
      <div class="max-w-7xl mx-auto flex items-center justify-between">
        <!-- Left: Branding & Lab Info -->
        <div class="flex items-center gap-4">
          <h1 class="text-xl font-bold text-indigo-600 dark:text-indigo-400">Autolabs</h1>
          <span class="text-gray-300 dark:text-gray-600">|</span>
          <div class="flex flex-col">
            <h2 class="text-lg font-semibold text-gray-900 dark:text-white">
              {{ labName || 'Share Portal' }}
            </h2>
            <span class="text-xs text-gray-500 dark:text-gray-400">
              Lab ID: <span class="font-mono">{{ labId || 'Loading...' }}</span>
            </span>
          </div>
        </div>

        <!-- Right: Session Info & Logout -->
        <div class="flex items-center gap-4">
          <!-- Session Expiry Warning -->
          <Alert v-if="isExpiringSoon && !isExpired" variant="destructive" class="py-2 px-3">
            <AlertTriangle class="w-4 h-4" />
            <AlertDescription class="text-xs">
              Session expires in {{ timeRemainingFormatted }}
            </AlertDescription>
          </Alert>

          <!-- Session Timer (Normal) -->
          <div
            v-else-if="!isExpired"
            class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
          >
            <Clock class="w-4 h-4" />
            <span>{{ timeRemainingFormatted }}</span>
          </div>

          <!-- Logout Button -->
          <Button variant="outline" size="sm" @click="handleLogout" class="gap-2">
            <LogOut class="w-4 h-4" />
            Logout
          </Button>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 py-6">
      <!-- Tabs -->
      <Tabs v-model="activeTab" class="w-full">
        <TabsList class="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="vms" class="gap-2">
            <Server class="w-4 h-4" />
            Virtual Machines
          </TabsTrigger>
          <TabsTrigger value="files" class="gap-2">
            <FolderOpen class="w-4 h-4" />
            Files
          </TabsTrigger>
        </TabsList>

        <!-- VMs Tab -->
        <TabsContent value="vms" class="mt-6">
          <!-- Loading State -->
          <div v-if="isLoading" class="flex flex-col items-center justify-center py-24">
            <LoadingSpinner size="lg" />
            <p class="mt-4 text-gray-600 dark:text-gray-400">Loading virtual machines...</p>
          </div>

          <!-- Error State -->
          <Card v-else-if="error" class="max-w-2xl mx-auto">
            <CardContent class="pt-6">
              <Alert variant="destructive" class="mb-4">
                <AlertDescription>{{ error }}</AlertDescription>
              </Alert>
              <div class="flex justify-center">
                <Button @click="fetchVMs">
                  <RefreshCw class="w-4 h-4 mr-2" />
                  Retry
                </Button>
              </div>
            </CardContent>
          </Card>

          <!-- VM List -->
          <Card v-else-if="vms.length > 0">
            <!-- Refresh Header -->
            <CardHeader class="flex flex-row items-center justify-between pb-2">
              <CardTitle>Virtual Machines</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                @click="fetchVMs"
                :disabled="isLoading"
                class="gap-2"
              >
                <RefreshCw :class="['w-4 h-4', isLoading && 'animate-spin']" />
                Refresh
              </Button>
            </CardHeader>
            <CardContent class="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>OS</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead class="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow v-for="vm in vms" :key="vm.id">
                    <!-- VM Name -->
                    <TableCell class="font-medium">
                      <div class="flex items-center gap-2">
                        <Monitor class="w-4 h-4 text-gray-400" />
                        {{ vm.name }}
                      </div>
                    </TableCell>

                    <!-- OS -->
                    <TableCell class="text-gray-600 dark:text-gray-400">
                      {{ vm.os || 'N/A' }}
                    </TableCell>

                    <!-- Status Badge -->
                    <TableCell>
                      <span
                        :class="[
                          'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize',
                          getStatusBadgeClasses(vm.status)
                        ]"
                      >
                        {{ vm.status }}
                      </span>
                    </TableCell>

                    <!-- Actions -->
                    <TableCell class="text-right">
                      <div class="flex items-center justify-end gap-2">
                        <!-- Loading State for VM -->
                        <div
                          v-if="isVMPending(vm.id)"
                          class="flex items-center gap-2 text-sm text-gray-500"
                        >
                          <Loader2 class="w-4 h-4 animate-spin" />
                          <span>Processing...</span>
                        </div>

                        <!-- Action Buttons -->
                        <template v-else>
                          <!-- Start Button -->
                          <Button
                            v-if="vm.status === 'stopped'"
                            size="sm"
                            @click="handlePowerControl(vm.id, 'start')"
                            class="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <Play class="w-3 h-3" />
                            Start
                          </Button>

                          <!-- Stop Button -->
                          <Button
                            v-if="vm.status === 'running'"
                            size="sm"
                            variant="outline"
                            @click="handlePowerControl(vm.id, 'stop')"
                            class="gap-1"
                          >
                            <Square class="w-3 h-3" />
                            Stop
                          </Button>

                          <!-- Restart Button -->
                          <Button
                            v-if="vm.status === 'running'"
                            size="sm"
                            variant="outline"
                            @click="handlePowerControl(vm.id, 'restart')"
                            class="gap-1"
                          >
                            <RefreshCw class="w-3 h-3" />
                            Restart
                          </Button>

                          <!-- Console Button -->
                          <Button
                            v-if="vm.status === 'running'"
                            size="sm"
                            @click="openConsole(vm.id)"
                            class="gap-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                          >
                            <Monitor class="w-3 h-3" />
                            Console
                          </Button>
                        </template>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <!-- Empty State -->
          <Card v-else class="max-w-2xl mx-auto">
            <CardContent class="flex flex-col items-center justify-center py-12">
              <Monitor class="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No Virtual Machines
              </h3>
              <p class="text-gray-600 dark:text-gray-400 text-center">
                This shared lab does not have any virtual machines available.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <!-- Files Tab -->
        <TabsContent value="files" class="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>File Management</CardTitle>
            </CardHeader>
            <CardContent>
              <ShareFileExplorer :share-id="shareId" initial-path="/" />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  </div>
</template>
